import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Typography} from '@mui/material'
import omit from 'lodash/omit'
import Card from 'src/components-new/layout/Card'
import ChartCard from 'src/components-new/data/ChartCard'
import EmptyState from 'src/components-new/layout/EmptyState'
import StatCard from 'src/components-new/data/StatCard'
import {buildChartOptions, ChartPresetType, hasChartData} from 'src/components-new/data/chartOptions'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import {ITALIAN_REGIONS} from 'src/constants/mock/county'
import {PATHS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import QuestionnaireChartCard from './QuestionnaireChartCard'
import SecondaryTherapyAdherenceCard, {SecondaryTherapyAdherence} from './SecondaryTherapyAdherenceCard'
import {calculateMaxCompilation, hasCompilationIndexedGraphs, processGraphData, processNyhaData} from './summaryUtils'

// Defined locally rather than imported from src/pages/home/Home — legacy imports are
// blocked under pages-new/ by the no-restricted-imports ESLint rule.
interface AdherenceType {
  pspAdherencePercentage?: number
  totalAdherencePercentage?: number
  pspAdherencePercentageYear?: number
  totalAdherencePercentageYear?: number
}

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

// Same fallback palette as legacy Home.tsx's `defaultColors` — Highcharts series
// colors (data visualization), not themeable UI surfaces, kept 1:1 with the legacy
// dashboard so charts look identical whichever style (legacy/new) renders them.
const DEFAULT_CHART_COLORS = ['#A5C842', '#495CA2', '#028B96', '#E87D1E', '#003DA5', '#085AA6', '#731472', '#3599B8']

interface ChartPanelProps {
  title: string
  type: ChartPresetType
  data: any
  colors: string[]
  legend?: boolean
  percentage?: boolean
  status?: string
  fullWidth?: boolean
}

// Local wrapper replicating legacy Home.tsx's per-panel behaviour: a spinner while
// `status` is PENDING, an EmptyState when the fetched data is empty, or a ChartCard
// (the shared components-new/data chart panel) once there's something to show.
function ChartPanel({title, type, data, colors, legend, percentage, status, fullWidth}: ChartPanelProps): JSX.Element {
  const {userGroup} = useAppSelector(state => state.userReducer.data)
  const sx = fullWidth ? {gridColumn: '1 / -1'} : undefined

  if (status === STATUS.PENDING) {
    return (
      <Card title={title} sx={sx}>
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={28} />
        </Box>
      </Card>
    )
  }

  if (!hasChartData(data)) {
    return (
      <Card title={title} sx={sx}>
        <EmptyState title="Nessun dato disponibile" />
      </Card>
    )
  }

  return (
    <ChartCard
      title={title}
      options={buildChartOptions({type, data, colors, legend, percentage, userGroup})}
      sx={sx}
    />
  )
}

export default function SummaryNew(): JSX.Element {
  const {summaryConfiguration, scope, theme: themeColors} = useAppSelector(state => state.pspConfigReducer.data)
  const {firstName: userFirstName, lastName: userLastName} = useAppSelector(state => state.userReducer.data)
  const kpiIdentifiers: Record<string, boolean> = useMemo(() => summaryConfiguration?.kpiIdentifiers ?? {}, [summaryConfiguration])

  const [status, setStatus] = useState<string>()
  const [filters, setFilters] = useState<Record<string, any>>({})

  const [patientSex, setPatientSex] = useState<any[]>([])
  const [patientCurrentAge, setPatientCurrentAge] = useState<any[]>([])
  const [patientStatus, setPatientStatus] = useState<any[]>([])
  const [patientVialDosages, setPatientVialDosages] = useState<any[]>([])
  const [patientDrug, setPatientDrug] = useState<any[]>([])
  const [patientMonthlyAdherence, setPatientMonthlyAdherence] = useState<any>({})
  const [patientRegionResidence, setPatientRegionResidence] = useState<any>({})
  const [doctorRegionResidence, setDoctorRegionResidence] = useState<any>({})
  const [patientServices, setPatientServices] = useState<any[]>([])
  const [mdsActive, setMdsActive] = useState<any[]>([])
  const [skippedAssumption, setSkippedAssumption] = useState<any[]>([])
  const [patientAdherenceActivations, setPatientAdherenceActivations] = useState<any>({})
  const [monthlyDrugAdherence, setMonthlyDrugAdherence] = useState<any>({})
  const [secondaryTherapyAdherence, setSecondaryTherapyAdherence] = useState<SecondaryTherapyAdherence>()
  const [assumptionsRegion, setAssumptionsRegion] = useState<any>({})
  const [regionAdherence, setRegionAdherence] = useState<any>({})
  const [nyhaScores, setNyhaScores] = useState<any>(undefined)
  const [questionnaireScores, setQuestionnaireScores] = useState<any[]>([])
  const [questionnaireGraphs, setQuestionnaireGraphs] = useState<Record<string, any[]>>({})
  const [tabValues, setTabValues] = useState<Record<string, string>>({})
  const [maxCompilation, setMaxCompilation] = useState(20)

  const [adherenceValues, setAdherenceValues] = useState<AdherenceType>({})
  const [colors, setColors] = useState<string[]>(
    (scope === 'quiperte' && themeColors)
      ? [themeColors?.primaryColor, themeColors?.secondaryColor].concat(DEFAULT_CHART_COLORS)
      : DEFAULT_CHART_COLORS,
  )

  const [statusDownload, setStatusDownload] = useState<string>()
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const fetchData = useCallback(() => {
    apiClient.getGraphs(filters, setStatus)
      .then((fetchedData: any) => {
        const data = fetchedData ?? {}
        setPatientSex(data.patientSex || [])
        setPatientCurrentAge(data.patientCurrentAge || [])
        setPatientStatus(data.patientStatus || [])
        setPatientRegionResidence(data.patientRegionResidence || {})
        setDoctorRegionResidence(data.doctorRegionResidence || {})
        setPatientMonthlyAdherence(data.patientMonthlyAdherence || {})
        setPatientDrug(data.patientDrug || [])
        setPatientServices(data.patientServices || [])
        setMdsActive(data.mdsActive || [])
        setSkippedAssumption(data.skippedAssumption || [])
        setPatientAdherenceActivations(data.patientAdherenceActivations || {})
        setMonthlyDrugAdherence(data.monthlyDrugAdherence || {})
        setSecondaryTherapyAdherence(data.secondaryTherapyAdherence)
        setAssumptionsRegion(data.assumptionsRegion || {})
        setRegionAdherence(data.regionAdherence || {})
        setPatientVialDosages(data.patientVialDosages || [])
        setNyhaScores(processNyhaData(data.nyhaScores || {}, filters))
        setQuestionnaireScores(data.questionnaireScores || [])
        setQuestionnaireGraphs(processGraphData(data.questionnaireScores || [], filters))
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [filters])

  // The drug filter belongs to the secondary therapy card only: summary/adherence rejects a key it does not know.
  const fetchAdherence = useCallback(() => {
    apiClient.getSummaryAdherence(omit(filters, 'secondaryDrugName'))
      .then((fetchedData: AdherenceType) => setAdherenceValues(fetchedData ?? {}))
      .catch(() => undefined)
  }, [filters])

  const fetchProgram = useCallback(() => {
    apiClient.getProgram()
      .then((program: any) => {
        if (program) {
          setColors(
            (scope === 'quiperte' && themeColors)
              ? [themeColors?.primaryColor, themeColors?.secondaryColor].concat(program.programPalette.colors)
              : program.programPalette.colors,
          )
        }
      })
      .catch(() => undefined)
  }, [scope, themeColors])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { fetchAdherence() }, [fetchAdherence])
  useEffect(() => { fetchProgram() }, [fetchProgram])

  useEffect(() => {
    if (Object.keys(questionnaireGraphs).length > 0) {
      const initialTabValues: Record<string, string> = {}
      for (const key in questionnaireGraphs) {
        if (questionnaireGraphs[key] && questionnaireGraphs[key].length > 0) {
          initialTabValues[key] = questionnaireGraphs[key][0].key
        }
      }
      setTabValues(initialTabValues)
    }
  }, [questionnaireGraphs])

  useEffect(() => {
    if (questionnaireScores || nyhaScores) {
      setMaxCompilation(calculateMaxCompilation(questionnaireScores, nyhaScores))
    }
  }, [questionnaireScores, nyhaScores])

  const handleTabChange = (graphKey: string, newValue: string) => {
    setTabValues(prev => ({...prev, [graphKey]: newValue}))
  }

  // Options of the drug filter of the secondary therapy adherence card: the drugs of the last response, each once
  // (exact string, in the order they arrive), plus the applied one when it is no longer among them (another filter
  // removed its patients): the select never goes blank and the user can always go back to "Tutti i farmaci".
  const secondaryDrugOptions = useMemo(() => {
    const drugNames = new Set(secondaryTherapyAdherence?.drugNames)
    if (filters.secondaryDrugName) drugNames.add(filters.secondaryDrugName)
    return [{value: '', label: 'Tutti i farmaci'}, ...[...drugNames].map(name => ({value: name, label: name}))]
  }, [secondaryTherapyAdherence, filters.secondaryDrugName])

  const filterConfig: FilterItem[] = useMemo(() => [
    ...(kpiIdentifiers.patientSex ? [{
      id: 'sex',
      label: 'Filtra pazienti per genere',
      type: 'select' as const,
      options: [
        {value: '', label: 'Tutti i generi'},
        {value: 'MALE', label: 'Maschio'},
        {value: 'FEMALE', label: 'Femmina'},
      ],
    }] : []),
    ...(kpiIdentifiers.patientStatus ? (summaryConfiguration?.patientsStateFilters ?? []) : []),
    ...(kpiIdentifiers.doctorStatus ? [{
      id: 'doctorStatus',
      label: 'Filtra medici per stato',
      type: 'select' as const,
      options: [
        {value: '', label: 'Tutti gli stati'},
        {value: 'IN_ACTIVATION', label: 'In attivazione'},
        {value: 'ACTIVE', label: 'Attivo'},
        {value: 'SUSPENDED', label: 'Sospeso'},
        {value: 'EXITED', label: 'Drop-Out'},
      ],
    }] : []),
    ...(kpiIdentifiers.patientDrug ? (summaryConfiguration?.drugFilters ?? []) : []),
    ...(kpiIdentifiers.patientRegionResidence ? [{
      id: 'patientRegion',
      label: 'Filtra pazienti per regione di residenza',
      type: 'select' as const,
      options: ITALIAN_REGIONS,
    }] : []),
    ...(kpiIdentifiers.patientCurrentAge ? [{
      id: 'patientAgeRange',
      label: "Filtra pazienti per fasce d'età",
      type: 'select' as const,
      options: [
        {value: '', label: "Tutte le fasce d'età"},
        {value: 'FROM_0_TO_6', label: 'Da 0 a 5 anni'},
        {value: 'FROM_6_TO_13', label: 'Da 6 a 13 anni'},
        {value: 'FROM_14_TO_17', label: 'Da 14 a 17 anni'},
        {value: 'FROM_18_TO_25', label: 'Da 18 a 25 anni'},
        {value: 'FROM_26_TO_40', label: 'Da 26 a 40 anni'},
        {value: 'FROM_41_TO_60', label: 'Da 41 a 60 anni'},
        {value: 'FROM_61_TO_80', label: 'Da 61 a 80 anni'},
        {value: 'FROM_81_TO_END', label: 'Oltre i 80 anni'},
      ],
    }] : []),
    ...(kpiIdentifiers.secondaryTherapyAdherence ? [{
      id: 'secondaryDrugName',
      label: 'Filtra aderenza per farmaco',
      type: 'select' as const,
      options: secondaryDrugOptions,
    }] : []),
    {id: 'date', label: 'Data', type: 'rangePicker' as const},
    // Kept visible while an interval is applied, so the user can always reset it.
    ...((hasCompilationIndexedGraphs(questionnaireGraphs, nyhaScores, kpiIdentifiers.nyhaScores)
      || filters.startInterval !== undefined || filters.endInterval !== undefined) ? [{
      id: 'compilationInterval',
      label: 'Intervallo di compilazioni',
      type: 'range' as const,
      min: 0,
      max: maxCompilation,
      step: 1,
      valueLabelFormat: (value?: number) => `T${value}`,
    }] : []),
  ], [kpiIdentifiers, summaryConfiguration, secondaryDrugOptions, maxCompilation, questionnaireGraphs, nyhaScores, filters])

  const onSubmit = (newFilters: FilterResult[]) => {
    const filtersValues = newFilters.reduce((result: Record<string, any>, item) => {
      if (item.type === 'rangePicker') {
        result.startDate = item.value?.[0]
        result.endDate = item.value?.[1]
        return result
      }
      if (item.type === 'range') {
        result.startInterval = item.value?.[0]
        result.endInterval = item.value?.[1]
        return result
      }
      result[item.property] = item.value
      return result
    }, {} as Record<string, any>)
    setFilters(filtersValues)
  }

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handleExport = async () => {
    const fileResponse = await apiClient.generatePdf(
      `${new URL(window.location.href).origin}/${PATHS.PDF_SUMMARY}?filters=${JSON.stringify({filters})}`,
      setStatusDownload,
    )
    if (!fileResponse) return
    const blob = new Blob([new Uint8Array(fileResponse)], {type: 'application/pdf'})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState({
      open: true,
      filePath: url,
      mime: 'application/pdf',
      fileName: `Riepilogo - ${userFirstName} ${userLastName}`,
      status: STATUS.FULFILLED,
    })
  }

  return (
    <Box>
      {status === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          Impossibile caricare i dati del riepilogo.
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        {summaryConfiguration?.enableExport && (
          <Button variant="primary" onClick={handleExport}>Esporta</Button>
        )}
        <Filters config={filterConfig} onSubmit={onSubmit} />
      </Box>

      {summaryConfiguration?.averagePatientTrend && (
        <Card title="Andamento medio pazienti" sx={{mb: 2}}>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, flex: '1 1 320px'}}>
              {(summaryConfiguration.adherenceKeys ?? []).map((item: {label: string, key: string}) => (
                <StatCard
                  key={item.key}
                  value={adherenceValues?.[item.key as keyof AdherenceType] ?? '-'}
                  label={item.label}
                  sx={{flex: '1 1 140px'}}
                />
              ))}
            </Box>
            <Box sx={{flex: '1 1 280px'}}>
              <Typography variant="body2">{summaryConfiguration.adherenceDescription}</Typography>
            </Box>
          </Box>
        </Card>
      )}

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2}}>
        {kpiIdentifiers.patientStatus && (
          <ChartPanel title="Pazienti per stato" type="pie" data={patientStatus} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.patientServices && (
          <ChartPanel title="Distribuzione dei pazienti per tipologia di servizio" type="pie" data={patientServices} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.patientSex && (
          <ChartPanel title="Pazienti per genere" type="pie" data={patientSex} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.patientCurrentAge && (
          <ChartPanel title="Pazienti per fasce d'età" type="pie" data={patientCurrentAge} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.mdsActive && (
          <ChartPanel title="Attivazioni MDS" type="pie" data={mdsActive} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.skippedAssumption && (
          <ChartPanel title="Distribuzione delle infusioni saltate" type="pie" data={skippedAssumption} colors={colors} legend={false} status={status} />
        )}
        {kpiIdentifiers.patientDrug && (
          <ChartPanel title="Pazienti per farmaco" type="pie" data={patientDrug} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.patientVialDosages && (
          <ChartPanel title="Fiala in uso" type="pie" data={patientVialDosages} colors={colors} legend status={status} />
        )}
        {kpiIdentifiers.patientRegionResidence && (
          <ChartPanel title="Numero Pazienti per regione" type="column" data={patientRegionResidence} colors={colors} status={status} fullWidth />
        )}
        {kpiIdentifiers.assumptionsRegion && (
          <ChartPanel title="Numero di infusioni per regione del paziente" type="column" data={assumptionsRegion} colors={colors} status={status} fullWidth />
        )}
        {kpiIdentifiers.patientAdherenceActivations && (
          <ChartPanel title="Numero di adesioni/attivazioni per mese" type="column" data={patientAdherenceActivations} colors={colors} status={status} fullWidth />
        )}
        {kpiIdentifiers.doctorRegionResidence && (
          <ChartPanel title="Numero Medici per regione" type="column" data={doctorRegionResidence} colors={colors} status={status} fullWidth />
        )}
        {kpiIdentifiers.patientMonthlyAdherence && (
          <ChartPanel title="Dati di aderenza per mese" type="stacking-column" data={patientMonthlyAdherence} colors={colors} percentage status={status} fullWidth />
        )}
        {kpiIdentifiers.regionAdherence && (
          <ChartPanel title="Dati di aderenza per regione" type="stacking-column" data={regionAdherence} colors={colors} percentage status={status} fullWidth />
        )}
        {kpiIdentifiers.monthlyDrugAdherence && (
          <ChartPanel title="Dati di aderenza tutor per mese per farmaco" type="column" data={monthlyDrugAdherence} colors={colors} percentage status={status} fullWidth />
        )}
        {kpiIdentifiers.secondaryTherapyAdherence && (
          <SecondaryTherapyAdherenceCard data={secondaryTherapyAdherence} status={status} />
        )}
        {kpiIdentifiers.nyhaScores && nyhaScores && (
          <ChartPanel title="Aggregato NYHA" type="nyha" data={nyhaScores} colors={colors} percentage status={status} fullWidth />
        )}
        {Object.keys(questionnaireGraphs).map((graphKey) => (
          <QuestionnaireChartCard
            key={graphKey}
            graphKey={graphKey}
            graphs={questionnaireGraphs[graphKey]}
            selectedTabKey={tabValues[graphKey]}
            status={status}
            onTabChange={handleTabChange}
          />
        ))}
      </Box>

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
